import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Palette, Megaphone, Image as ImageIcon, Save, Loader2, Upload,
  Sun, Moon, Mail, Phone, Eye, RefreshCw, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HEX_RE = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const DEFAULT_BRANDING = {
  logo_url: null,
  announcement_enabled: false,
  announcement_text: '',
  announcement_bg_color: '#6d28d9',
  announcement_text_color: '#ffffff',
  primary_color: '#8b5cf6',
  accent_color: '#a78bfa',
  theme: 'dark',
  support_email: null,
  support_phone: null,
  footer_note: null,
};

const ColorInput = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={HEX_RE.test(value || '') ? value : '#000000'}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-12 rounded cursor-pointer bg-transparent border border-white/10"
      data-testid={`${testid}-picker`}
    />
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="#aabbcc"
      className="bg-white/5 border-white/10 font-mono text-sm w-32"
      data-testid={`${testid}-text`}
    />
  </div>
);

export const ReturnPortalBrandingTab = () => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE_URL}/v1/returns/portal-branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranding({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
    } catch (e) {
      toast.error('Failed to load branding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (key, value) => setBranding((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    // Client-side hex check so users get instant feedback
    for (const f of ['announcement_bg_color', 'announcement_text_color', 'primary_color', 'accent_color']) {
      if (branding[f] && !HEX_RE.test(branding[f])) {
        toast.error(`${f.replace(/_/g, ' ')} must be a hex like #aabbcc`);
        return;
      }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(
        `${API_BASE_URL}/v1/returns/portal-branding`,
        branding,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBranding({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
      toast.success('Portal branding saved');
    } catch (e) {
      // Surface the precise field error so the merchant can act on it
      // ("footer_note: too long" instead of just "Failed to save"). The
      // backend now returns either a string detail or a list of
      // {loc, msg} dicts depending on which validation path tripped.
      const raw = e.response?.data?.detail;
      let msg = 'Failed to save branding';
      if (typeof raw === 'string' && raw.trim()) {
        msg = raw.split('\n')[0].slice(0, 200);
      } else if (Array.isArray(raw) && raw.length) {
        const f = raw[0];
        const loc = (f?.loc || []).filter((p) => p !== 'body').join('.');
        msg = `${loc || 'request'}: ${f?.msg || 'invalid'}`;
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API_BASE_URL}/v1/returns/portal-branding/logo`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      patch('logo_url', r.data.logo_url);
      toast.success('Logo uploaded — click Save to apply');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* Editor */}
      <div className="lg:col-span-3 space-y-4" data-testid="branding-editor">
        {/* Logo */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-purple-400" />
            Logo
          </h3>
          <div className="flex items-center gap-3">
            <div className="h-20 w-20 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="logo preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <ImageIcon className="w-8 h-8 text-zinc-600" />
              )}
            </div>
            <div className="flex-1 flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                className="hidden"
                onChange={handleLogoUpload}
                data-testid="branding-logo-file-input"
              />
              <Button
                variant="outline"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="branding-logo-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {branding.logo_url ? 'Replace logo' : 'Upload logo'}
              </Button>
              {branding.logo_url && (
                <Button
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => patch('logo_url', null)}
                  data-testid="branding-logo-remove-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-zinc-500">PNG, JPG, WebP, SVG or GIF. Max 2 MB. Recommended height 40–80 px.</p>
        </div>

        {/* Announcement bar */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-purple-400" />
              Announcement Bar
            </h3>
            <Switch
              checked={!!branding.announcement_enabled}
              onCheckedChange={(v) => patch('announcement_enabled', v)}
              data-testid="branding-announcement-toggle"
            />
          </div>
          {branding.announcement_enabled && (
            <div className="space-y-3">
              <div>
                <Label>Message</Label>
                <Textarea
                  value={branding.announcement_text || ''}
                  onChange={(e) => patch('announcement_text', e.target.value)}
                  rows={2}
                  maxLength={240}
                  className="bg-white/5 border-white/10 mt-1"
                  placeholder="e.g., Free returns within 30 days · Easy self-serve · Support available 7 days a week"
                  data-testid="branding-announcement-text"
                />
                <p className="text-[11px] text-zinc-600 mt-1">{(branding.announcement_text || '').length}/240</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Background</Label>
                  <div className="mt-1">
                    <ColorInput value={branding.announcement_bg_color} onChange={(v) => patch('announcement_bg_color', v)} testid="branding-announcement-bg" />
                  </div>
                </div>
                <div>
                  <Label>Text color</Label>
                  <div className="mt-1">
                    <ColorInput value={branding.announcement_text_color} onChange={(v) => patch('announcement_text_color', v)} testid="branding-announcement-text-color" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Colors + theme */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Palette className="w-4 h-4 text-purple-400" />
            Theme &amp; Colors
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Primary color (buttons)</Label>
              <div className="mt-1">
                <ColorInput value={branding.primary_color} onChange={(v) => patch('primary_color', v)} testid="branding-primary-color" />
              </div>
            </div>
            <div>
              <Label>Accent color (highlights)</Label>
              <div className="mt-1">
                <ColorInput value={branding.accent_color} onChange={(v) => patch('accent_color', v)} testid="branding-accent-color" />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Portal theme</Label>
              <Select
                value={branding.theme || 'dark'}
                onValueChange={(v) => patch('theme', v)}
              >
                <SelectTrigger className="bg-white/5 border-white/10 mt-1 w-48" data-testid="branding-theme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="dark"><Moon className="w-3 h-3 inline mr-2" />Dark</SelectItem>
                  <SelectItem value="light"><Sun className="w-3 h-3 inline mr-2" />Light</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Support contacts + footer */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-400" />
            Support &amp; Footer
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Support email</Label>
              <Input
                type="email"
                value={branding.support_email || ''}
                onChange={(e) => patch('support_email', e.target.value)}
                placeholder="support@brand.com"
                className="bg-white/5 border-white/10 mt-1"
                data-testid="branding-support-email"
              />
            </div>
            <div>
              <Label>Support phone</Label>
              <Input
                value={branding.support_phone || ''}
                onChange={(e) => patch('support_phone', e.target.value)}
                placeholder="+91 1234567890"
                className="bg-white/5 border-white/10 mt-1"
                data-testid="branding-support-phone"
              />
            </div>
            <div className="col-span-2">
              <Label>Footer note (optional)</Label>
              <Textarea
                value={branding.footer_note || ''}
                onChange={(e) => patch('footer_note', e.target.value)}
                rows={2}
                maxLength={500}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="e.g., All returns are processed within 2 business days."
                data-testid="branding-footer-note"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="outline"
            className="border-white/10"
            onClick={load}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Discard
          </Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleSave}
            disabled={saving}
            data-testid="branding-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save branding
          </Button>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:col-span-2">
        <BrandingPreview branding={branding} />
      </div>
    </div>
  );
};

const BrandingPreview = ({ branding }) => {
  const theme = branding.theme || 'dark';
  const isDark = theme === 'dark';
  const bg = isDark ? '#0a0a0f' : '#f8fafc';
  const surface = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#ffffff' : '#0f172a';
  const textMuted = isDark ? '#a1a1aa' : '#64748b';

  return (
    <div className="sticky top-24 rounded-xl overflow-hidden border border-white/10 shadow-2xl" data-testid="branding-preview">
      <div className="px-3 py-2 text-[11px] text-zinc-500 bg-zinc-900/60 border-b border-white/5 flex items-center gap-2">
        <Eye className="w-3 h-3" /> Live customer portal preview
      </div>
      <motion.div
        initial={false}
        animate={{ backgroundColor: bg }}
        className="p-4 min-h-[480px]"
        style={{ color: textPrimary }}
      >
        {branding.announcement_enabled && (branding.announcement_text || '').trim() && (
          <div
            className="text-center py-2 px-3 text-xs rounded-md mb-4 font-medium"
            style={{
              backgroundColor: branding.announcement_bg_color,
              color: branding.announcement_text_color,
            }}
            data-testid="branding-preview-announcement"
          >
            {branding.announcement_text}
          </div>
        )}
        <div className="flex items-center gap-3 mb-4">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="brand logo" className="h-10 object-contain" data-testid="branding-preview-logo" />
          ) : (
            <div className="text-lg font-semibold">Your Brand</div>
          )}
          <span className="text-xs" style={{ color: textMuted }}>Returns &amp; Refunds</span>
        </div>

        <div
          className="rounded-xl p-4 mb-3"
          style={{ backgroundColor: surface, border: `1px solid ${border}` }}
        >
          <div className="text-sm font-medium mb-2">Order #1234</div>
          <div className="text-xs" style={{ color: textMuted }}>2 items selected · ₹1,499 estimated refund</div>
        </div>

        <button
          className="w-full py-2.5 rounded-lg text-sm font-medium transition-transform active:scale-[0.98]"
          style={{
            backgroundColor: branding.primary_color,
            color: '#ffffff',
          }}
          data-testid="branding-preview-cta"
        >
          Submit return request
        </button>

        <div className="mt-4 flex flex-col gap-1 text-[11px]" style={{ color: textMuted }}>
          {branding.support_email && (
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{branding.support_email}</div>
          )}
          {branding.support_phone && (
            <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{branding.support_phone}</div>
          )}
          {branding.footer_note && (
            <div className="italic mt-1">{branding.footer_note}</div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
